import { Calendar, Check, Clock, type LucideIcon } from 'lucide-react'
import { Button } from '~/components/ui/button'
import { Separator } from '~/components/ui/separator'
import { type Step, useBookingVisitStore } from './space-visit.store'
import { cn } from '~/lib/utils'
import { useMemo } from 'react'
import { useTranslation } from 'next-i18next'

const iconRefs: Record<Step, LucideIcon> = {
	intro: Calendar,
	day: Calendar,
	startTime: Clock,
	payment: Check,
}

const labelRefs: Record<Step, string> = {
	intro: '',
	day: 'space:visit.dialog.day.indicatorLabel',
	startTime: 'space:visit.dialog.startTime.indicatorLabel',
	payment: 'space:visit.dialog.payment.indicatorLabel',
}

const StepBtn = ({
	step,
	activeStep,
}: {
	step: Step
	activeStep: Step
}) => {
	const { t } = useTranslation()
	const setStep = useBookingVisitStore.use.setStep()

	const disabled = useMemo(() => {
		if (step === 'startTime') return ['day'].includes(activeStep)
		if (step === 'payment') return ['day', 'startTime'].includes(activeStep)
		return false
	}, [activeStep, step])

	const Icon = iconRefs[step]

	return (
		<Button
			variant={activeStep === step ? 'default' : 'outline'}
			className='!size-10 relative p-0'
			disabled={disabled}
			onClick={() => setStep(step)}
		>
			<Icon className='size-4 shrink-0' />

			<span
				className={cn(
					'-bottom-5 -translate-x-1/2 absolute left-1/2 font-normal text-foreground text-xs disabled:text-foreground/50',
					{ 'text-primary': activeStep === step },
				)}
			>
				{t(labelRefs[step])}
			</span>
		</Button>
	)
}

export const StepperIndicator = () => {
	const activeStep = useBookingVisitStore.use.step()

	if (activeStep === 'intro') return null

	return (
		<div className='my-2 flex items-center gap-4 pb-4'>
			<StepBtn
				step='day'
				activeStep={activeStep}
			/>

			<Separator
				className={cn('flex-1', {
					'bg-foreground': ['startTime', 'payment'].includes(activeStep),
				})}
			/>

			<StepBtn
				step='startTime'
				activeStep={activeStep}
			/>

			<Separator
				className={cn('flex-1', {
					'bg-foreground': activeStep === 'payment',
				})}
			/>

			<StepBtn
				step='payment'
				activeStep={activeStep}
			/>
		</div>
	)
}
